/**
 * FileContextIndicator Component
 * Shows attached files as a visual indicator with count
 */

import React from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';

export interface FileContextIndicatorProps {
  fileCount: number;
  files?: Array<{
    fileId: string;
    fileName: string;
    /** Set while a file's AI context is being analysed on demand, or if it failed. */
    analysisStatus?: 'analyzing' | 'ready' | 'error';
    analysisError?: string;
  }>;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple' | 'warning' | 'orange';
  onClick?: () => void;
}

export const FileContextIndicator: React.FC<FileContextIndicatorProps> = ({
  fileCount,
  files = [],
  variant = 'neutral',
  onClick
}) => {
  if (fileCount === 0) return null;

  // Analysis is now on demand, so a just-dropped file can be mid-analysis. Say so
  // rather than showing "1 file attached" over a context that is not there yet.
  const analyzing = files.filter(f => f?.analysisStatus === 'analyzing');
  const failed = files.filter(f => f?.analysisStatus === 'error');

  let label = `${fileCount} file${fileCount > 1 ? 's' : ''} attached`;
  if (analyzing.length > 0) {
    label = `Analyzing ${analyzing.length} file${analyzing.length > 1 ? 's' : ''}…`;
  } else if (failed.length > 0) {
    label = `${failed.length} file${failed.length > 1 ? 's' : ''} could not be analysed`;
  }

  const title = failed.length > 0
    ? failed.map(f => `${f.fileName}: ${f.analysisError || 'analysis failed'}`).join('\n')
    : files.map(f => f.fileName).join(', ');

  return (
    <div
      className={`file-context-indicator file-context-${variant}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      title={title}
    >
      <AttachFileIcon className="context-icon" sx={{ fontSize: '16px' }} />
      <span className="context-text">
        {label}
      </span>
    </div>
  );
};
